/**
 * The finance system, connected — the screen.
 *
 * Until now the executive desks took their money from a file somebody saved
 * out of SAP once a month and uploaded here. This is the same figures read
 * instead of carried, and the screen is built around the four questions
 * anybody sensible asks of a finance connector before trusting a number that
 * came through it:
 *
 *   what came back, and how old is it
 *   what did it refuse to count, and why
 *   whose money has nowhere to go
 *   does it agree with what this platform already knows
 *
 * The button that sends the figures to the desks is at the bottom, after all
 * four, because that order is the argument: a connector that shows its
 * totals first and its exclusions on a second tab is a connector that gets
 * trusted before it has been read.
 *
 * Where the platform is connected, the read is the platform's: it reads SAP
 * itself (backend/api/slt/finance/, by the same rules as `domain/sap.ts`) and
 * this screen shows its latest read — live, or a journal file sent to it —
 * with the SAP connection panel at the foot. Where it is not — the
 * demonstration, or a platform that has not read anything yet — the read runs
 * against a sample in SAP's own field shapes and the top of the screen says
 * so. Either way nothing else on the screen changes, which was the point of
 * building it against the sample first.
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { useSession } from "../../app/SessionContext";
import { hasPermission, PERMISSION } from "../../api/dataSource";
import { useScreenContext } from "../../assistant/screen";
import { Button, Card, EmptyState, Table, numClass } from "../../design/components";
import { Page } from "../../layout/AppShell";
import { useEntered } from "../executive/store";
import { demoFinance, type Finance } from "../../domain/finance";
import { FINANCE_TOPIC } from "../executive/FinanceForm";
import {
  ACCOUNT_RULES,
  freshness,
  readSap,
  readSentence,
  reconcile,
  REJECTION_LABEL,
  toFinance,
  type AccountRule,
  type SapRead,
  type Unbudgeted,
} from "../../domain/sap";
import { SapConnectionPanel } from "./SapConnectionPanel";
import { sapResponse } from "../../mock/sap";
import { locale, t, tn } from "../../i18n";
import s from "./FinanceSystem.module.css";

function money(n: number, currency: string): string {
  return `${Math.round(n).toLocaleString(locale())} ${currency}`;
}

/** Short money, for a tile: the magnitude is the point, not the halalas. */
function big(n: number, currency: string): string {
  const m = Math.abs(n);
  if (m >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}m ${currency}`;
  if (m >= 1_000) return `${Math.round(n / 1_000)}k ${currency}`;
  return money(n, currency);
}

export function FinanceSystemPage() {
  const session = useSession();
  const source = session.source;
  const { pathname } = useLocation();
  const now = new Date();
  const finance = useEntered<Finance>(FINANCE_TOPIC, demoFinance());
  const [sent, setSent] = useState<string | null>(null);

  /* The read. One sample, one clock — recomputed only when the hour turns, so
     the freshness line moves and nothing else flickers underneath it. The
     hour is pulled out as its own value because a dependency the linter
     cannot read statically is a dependency nobody can check. */
  const hour = Math.floor(now.getTime() / 3_600_000);
  const sample: SapRead = useMemo(() => readSap(sapResponse(new Date(hour * 3_600_000))), [hour]);

  /*
   * The platform's own read, where there is one: the live connection's latest,
   * or a journal file sent to it. Read by the same rules as the sample
   * (backend finance/read.py, held to the same fixture), so everything below
   * works on it unchanged. Until the platform has read anything — or where
   * this person may not see the finance system — the sample stands in, and
   * the notice at the top says which of the two this is.
   */
  const canRead = hasPermission(session.principal, PERMISSION.financeRead);
  const latest = useQuery({
    queryKey: ["finance-latest", session.site.id],
    queryFn: () => source.financeLatest(session.site.id),
    enabled: canRead,
    retry: false,
  });
  const live = latest.data?.read ?? null;
  const read: SapRead = live ?? sample;
  const rules: AccountRule[] = live?.rules?.length ? live.rules : ACCOUNT_RULES;
  const how = freshness(read, now);

  /*
   * What this platform's own records say, for the comparisons it can honestly
   * make. Two of them, and they behave differently on purpose.
   *
   * Maintenance comes from work orders that carry an actual cost. Many do
   * not, and those are left out rather than counted as nothing — so the
   * figure is a floor. Where no job carries a cost at all there is no
   * comparison to draw, and the screen says that rather than showing a zero
   * against SAP's millions.
   *
   * Capital comes from the project register, which is explicit about not
   * knowing: a project's money is `declared` or `not_declared`, and an
   * undeclared project contributes nothing and is counted in what the screen
   * admits it left out.
   */
  const orders = useQuery({
    queryKey: ["work-orders", session.site.id],
    queryFn: () => source.listWorkOrders({ siteId: session.site.id }),
  });
  const projects = useQuery({
    queryKey: ["projects", session.site.id],
    queryFn: () => source.listProjects({ siteId: session.site.id }),
  });

  const ownSpend = useMemo(() => {
    const costed = (orders.data?.items ?? []).filter((w) => typeof w.costActual === "number");
    if (costed.length === 0) return undefined;
    return { total: costed.reduce((n: number, w) => n + (w.costActual ?? 0), 0), jobs: costed.length };
  }, [orders.data]);

  const ownCapital = useMemo(() => {
    const rows = projects.data ?? [];
    const declared = rows.filter((p) => p.money.state === "declared" && typeof p.money.spentMinor === "number");
    if (declared.length === 0) return undefined;
    return {
      /* the platform holds money in minor units; SAP's figures are major */
      total: declared.reduce((n: number, p) => n + (p.money.spentMinor ?? 0), 0) / 100,
      projects: declared.length,
      silent: rows.length - declared.length,
    };
  }, [projects.data]);

  const checks = reconcile(read, { maintenanceSpend: ownSpend?.total, capexSpend: ownCapital?.total });

  const rejectedLines = read.rejected.reduce((n, r) => n + r.lines, 0);

  useScreenContext({
    route: pathname,
    title: "The finance system",
    /*
     * Read, not worked at — but the read IS the content, and hiding the
     * exclusions behind "open the full desk" would leave a summary of totals
     * and nothing about what is not in them. That is the one arrangement this
     * screen must not have.
     */
    worksInFull: true,
    summary: [readSentence(read), how.says, ...checks.map((c) => c.says)],
    offers: [
      { label: "What is not being counted?", ask: "what did the read refuse to count?" },
      { label: "Which accounts have no rule?", ask: "which accounts are unmapped?" },
      { label: "Does it agree with us?", ask: "does the finance system agree with the platform?" },
    ],
  });

  const send = async () => {
    await finance.save(
      toFinance(read, {
        period: t("{year} period {period}", { year: read.period.fiscalYear, period: read.period.fiscalPeriod }),
        daysElapsed: finance.value.daysElapsed,
        passengers: finance.value.passengers,
        monthly: finance.value.monthly,
        capexProjects: finance.value.capex,
      }),
    );
    setSent(
      t("Sent. The executive desks now read {n} from the finance system, and say where they came from.", {
        n: tn(read.mapped, "{n} journal line", "{n} journal lines"),
      }),
    );
  };

  return (
    <Page eyebrow={t("Financial System")} title={t("The finance system")}>
      <p className={s.says}>
        {t(
          "The S/4HANA journal, read directly, with anything left out shown beside the totals.",
        )}
      </p>
      {live ? (
        <div className={s.notice} data-testid="sap-live">
          <strong>{t("Read from the finance system.")}</strong>{" "}
          {latest.data?.run?.source === "file"
            ? t("Everything below is the journal file {name}, read on the platform by the same rules as a live read.", {
                name: latest.data.run.fileName ?? "",
              })
            : t("Everything below is the platform's latest read of {system}, for {year} period {period}.", {
                system: read.system,
                year: read.period.fiscalYear,
                period: read.period.fiscalPeriod,
              })}
        </div>
      ) : source.kind === "mock" ? (
        <div className={s.notice}>
          <strong>{t("Not connected.")}</strong>{" "}
          {t(
            "This build doesn't call SAP. Below is a sample read: the mapping, exclusions and checks are real, but the figures aren't the airport's.",
          )}
        </div>
      ) : (
        <div className={s.notice}>
          <strong>{t("Not read yet.")}</strong>{" "}
          {t(
            "The finance system hasn't been read for this site yet. Below is a sample read: the mapping, exclusions and checks are real, but the figures aren't the airport's. Set up the connection at the bottom of this page.",
          )}
        </div>
      )}

      <Card
        title="What came back"
        sub={t("{system} · {service}", { system: read.system, service: read.service })}
        collapsible
        testId="sap-read"
      >
        <p className={s.says}>{readSentence(read)}</p>
        <p className={how.stale ? s.stale : s.fresh}>{how.says}</p>

        <div className={s.tiles}>
          <div className={s.tile}>
            <span className={s.tileLabel}>{t("Revenue")}</span>
            <span className={s.tileValue}>{big(read.totals.revenue, read.currency)}</span>
            <span className={s.tileNote}>
              {tn(read.revenue.length, "{n} line", "{n} lines")}
            </span>
          </div>
          <div className={s.tile}>
            <span className={s.tileLabel}>{t("Operating cost")}</span>
            <span className={s.tileValue}>{big(read.totals.opex, read.currency)}</span>
            <span className={s.tileNote}>
              {tn(read.opex.length, "{n} area", "{n} areas")}
            </span>
          </div>
          <div className={s.tile}>
            <span className={s.tileLabel}>{t("Capital")}</span>
            <span className={s.tileValue}>{big(read.totals.capex, read.currency)}</span>
            <span className={s.tileNote}>{t("asset accounts")}</span>
          </div>
          <div className={s.tile}>
            <span className={s.tileLabel}>{t("Fiscal period")}</span>
            <span className={s.tileValue}>
              {read.period.fiscalYear}/{read.period.fiscalPeriod}
            </span>
            <span className={read.period.state === "open" ? s.tileWarn : s.tileNote}>
              {read.period.state === "open"
                ? t("open — these can still change")
                : t("closed")}
            </span>
          </div>
        </div>

        <h3 className={s.h3}>{t("Revenue")}</h3>
        <LineTable lines={read.revenue} currency={read.currency} unbudgeted={read.unbudgeted} />
        <h3 className={s.h3}>{t("Operating cost")}</h3>
        <LineTable lines={read.opex} currency={read.currency} unbudgeted={read.unbudgeted} />
        {!read.planned ? (
          <p className={s.warn}>
            {t(
              "No plan was read, so the plan column repeats the actual. Nothing is over or under plan; the plan isn't known.",
            )}
          </p>
        ) : (
          read.unbudgeted.some((u) => u.why === "partial") && (
            <p className={s.warn}>
              {t(
                "Where the plan covers fewer accounts than the actual, it's shown as coverage and no variance is calculated. Otherwise you'd see an overspend that isn't real.",
              )}
            </p>
          )
        )}
      </Card>

      <Card
        title="What it would not count"
        sub={t("Lines left out of every total, and why")}
        collapsible
        testId="sap-rejected"
      >
        {rejectedLines === 0 ? (
          <EmptyState>{t("Every line that came back was counted.")}</EmptyState>
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <th>{t("Why")}</th>
                  <th className={numClass}>{t("Lines")}</th>
                  <th className={numClass}>{t("Amount")}</th>
                </tr>
              </thead>
              <tbody>
                {read.rejected.map((r) => (
                  <tr key={r.reason}>
                    <td>{t(REJECTION_LABEL[r.reason])}</td>
                    <td className={numClass}>{r.lines}</td>
                    <td className={numClass}>{money(r.amount, read.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <p className={s.note}>
              {t(
                "Reversed documents and their reversals are both left out, since neither stands. Lines with no debit or credit indicator have no direction, so they're left out too.",
              )}
            </p>
          </>
        )}
      </Card>

      <Card
        title="Accounts no rule covers"
        sub={t("Not in any total above")}
        collapsible
        testId="sap-unmapped"
      >
        {read.unmapped.length === 0 ? (
          <EmptyState>{t("Every account that came back is covered by a rule.")}</EmptyState>
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <th>{t("G/L account")}</th>
                  <th>{t("Name")}</th>
                  <th className={numClass}>{t("Lines")}</th>
                  <th className={numClass}>{t("Amount")}</th>
                </tr>
              </thead>
              <tbody>
                {read.unmapped.map((u) => (
                  <tr key={u.account}>
                    <td className={s.mono}>{u.account}</td>
                    <td>{u.name}</td>
                    <td className={numClass}>{u.lines}</td>
                    <td className={numClass}>
                      {money(Math.abs(u.amount), read.currency)}{" "}
                      <span className={s.direction}>
                        {u.amount < 0 ? t("debit") : t("credit")}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <p className={s.note}>
              {t(
                "These aren't errors. They're accounts the mapping doesn't know yet, kept out of every total so they stay visible.",
              )}
            </p>
          </>
        )}
      </Card>

      <Card
        title="Compared with platform records"
        sub={t("Neither side is adjusted; the gap is the finding")}
        collapsible
        testId="sap-reconcile"
      >
        {checks.length === 0 ? (
          <EmptyState>
            {t(
              "Nothing to compare yet. No work order has an actual cost, and no capital project has reported spending.",
            )}
          </EmptyState>
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <th>{t("What")}</th>
                  <th className={numClass}>{t("Finance system")}</th>
                  <th className={numClass}>{t("This platform")}</th>
                  <th className={numClass}>{t("Difference")}</th>
                </tr>
              </thead>
              <tbody>
                {checks.map((c) => (
                  <tr key={c.key}>
                    <td>{c.label}</td>
                    <td className={numClass}>{money(c.ledger, read.currency)}</td>
                    <td className={numClass}>{money(c.platform, read.currency)}</td>
                    <td className={numClass}>
                      {c.difference >= 0 ? "+" : "−"}
                      {money(Math.abs(c.difference), read.currency)}
                      {c.pct !== null && <span className={s.pct}> ({c.pct}%)</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {ownSpend && (
              <p className={s.note}>
                {t(
                  "The maintenance figure is the actual cost on {n}. Jobs with no cost are left out, so it's a minimum, not a total.",
                  { n: tn(ownSpend.jobs, "{n} work order", "{n} work orders") },
                )}
              </p>
            )}
            {ownCapital && (
              <p className={s.note}>
                {t("The capital figure is what {n} have declared spending.", {
                  n: tn(ownCapital.projects, "{n} project", "{n} projects"),
                })}{" "}
                {ownCapital.silent > 0 &&
                  tn(
                    ownCapital.silent,
                    "One further project has declared no money at all and is in neither figure — the register records that it does not know, and this screen does not fill the gap in.",
                    "{n} further projects have declared no money at all and are in neither figure — the register records that it does not know, and this screen does not fill the gap in.",
                  )}
              </p>
            )}
            <p className={s.note}>
              {t(
                "The two systems count different things, so they won't match exactly. A gap is a question about scope, not necessarily an error.",
              )}
            </p>
          </>
        )}
      </Card>

      <Card
        title="The mapping"
        sub={t("which G/L accounts become which figure, and why")}
        collapsible
        testId="sap-rules"
      >
        <p className={s.note}>
          <strong>{t("Proposed.")}</strong>{" "}
          {t(
            "These are typical airport account ranges. Once connected, they come from your own chart of accounts. Each line shows the rule that placed it.",
          )}
        </p>
        <Table>
          <thead>
            <tr>
              <th>{t("Accounts")}</th>
              <th>{t("Becomes")}</th>
              <th>{t("Why")}</th>
            </tr>
          </thead>
          <tbody>
            {rules.map((r, i) => (
              <tr key={`${i}-${r.from}`}>
                <td className={s.mono}>
                  {r.from}–{r.to}
                </td>
                <td>
                  {r.target.kind === "ignore"
                    ? t("not counted")
                    : r.target.kind === "capex"
                      ? t("Capital")
                      : `${r.target.kind === "revenue" ? t("Revenue") : t("Operating cost")} · ${r.target.label}`}
                </td>
                <td className={s.why}>{r.target.kind === "ignore" ? r.target.why : r.why}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>

      <Card
        title="Send these to the executive desks"
        sub={t("Nine desks use these figures; today: {basis}", {
          basis:
            finance.value.basis === "demo"
              ? t("the demonstration's figures")
              : finance.value.basis === "ledger"
                ? t("a previous read")
                : t("figures entered by hand"),
        })}
        testId="sap-send"
      >
        <p className={s.note}>
          {t(
            "The desks will label each figure with its source, period and whether the period is closed. Excluded lines stay excluded.",
          )}
        </p>
        {read.period.state === "open" && (
          <p className={s.warn}>
            {t(
              "This period is still open, so its figures may change. The desks will say so.",
            )}
          </p>
        )}
        <Button variant="primary" onClick={send} disabled={finance.loading}>
          {t("Send the read to the desks")}
        </Button>
        {sent && <p className={s.sent}>{sent}</p>}
        {finance.savedTo && <p className={s.note}>{finance.savedTo}</p>}
      </Card>

      {/* Last, after the send: setting up where the figures come from is
          done once; reading them is what the screen is for. */}
      {canRead && (
        <SapConnectionPanel
          source={source}
          siteId={session.site.id}
          canConnect={hasPermission(session.principal, PERMISSION.financeConnect)}
          canImport={hasPermission(session.principal, PERMISSION.financeImport)}
        />
      )}
    </Page>
  );
}

function LineTable({
  lines,
  currency,
  unbudgeted,
}: {
  lines: { key: string; label: string; actual: number; budget: number }[];
  currency: string;
  unbudgeted: Unbudgeted[];
}) {
  if (lines.length === 0) return <EmptyState>{t("Nothing mapped here.")}</EmptyState>;
  return (
    <Table>
      <thead>
        <tr>
          <th>{t("Line")}</th>
          <th className={numClass}>{t("Actual")}</th>
          <th className={numClass}>{t("Plan")}</th>
        </tr>
      </thead>
      <tbody>
        {lines.map((l) => {
          const gap = unbudgeted.find((u) => u.key === l.key);
          return (
            <tr key={l.key}>
              <td>{l.label}</td>
              <td className={numClass}>{money(l.actual, currency)}</td>
              <td className={numClass}>
                {!gap ? (
                  money(l.budget, currency)
                ) : gap.why === "none" ? (
                  <span className={s.noPlan}>{t("no plan read")}</span>
                ) : (
                  /* a plan exists and is smaller than the line: say how much
                     smaller rather than setting it against the whole actual */
                  <span className={s.noPlan}>
                    {t("plan covers {planned} of {accounts} accounts", {
                      planned: gap.planned,
                      accounts: gap.accounts,
                    })}
                  </span>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
}
